import type { IntegrationType } from "@v2/features/settings/shop/integrations/IntegrationForm";

import * as React from "react";

import { Button } from "@replo/design-system/components/button/Button";
import { Tag } from "@replo/design-system/components/tag/Tag";
import useCurrentProjectId from "@v2/hooks/useCurrentProjectId";
import { routes } from "@v2/utils/router";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";
import { generatePath, useNavigate } from "react-router-dom";

import IntegrationCompactCard from "./IntegrationCompactCard";

type EnabledIntegrationsRowProps = {
  connectedTypes: IntegrationType[];
};

const SCROLL_AMOUNT = 200;

const EnabledIntegrationsRow: React.FC<EnabledIntegrationsRowProps> = ({
  connectedTypes,
}) => {
  const navigate = useNavigate();
  const projectId = useCurrentProjectId();
  const scrollRef = React.useRef<HTMLDivElement>(null);
  const [canScrollLeft, setCanScrollLeft] = React.useState(false);
  const [canScrollRight, setCanScrollRight] = React.useState(false);

  const checkOverflow = React.useCallback(() => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth - 1);
  }, []);

  React.useEffect(() => {
    checkOverflow();
    const el = scrollRef.current;
    if (!el) {
      return;
    }

    const observer = new ResizeObserver(checkOverflow);
    observer.observe(el);
    return () => observer.disconnect();
  }, [checkOverflow]);

  const scroll = (direction: "left" | "right") => {
    const el = scrollRef.current;
    if (!el) {
      return;
    }
    el.scrollBy({
      left: direction === "right" ? SCROLL_AMOUNT : -SCROLL_AMOUNT,
      behavior: "smooth",
    });
  };

  const handleScroll = () => {
    checkOverflow();
  };

  if (connectedTypes.length === 0) {
    return null;
  }

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-sm font-medium text-default">Enabled</span>
          <Tag variant="neutral" size="sm">
            {connectedTypes.length}
          </Tag>
        </div>
        <Button
          variant="tertiary"
          size="sm"
          endEnhancer={<ArrowRight size={12} />}
          onClick={() => {
            navigate(
              generatePath(routes.shops.integrations.enabled, {
                projectId: projectId ?? "",
              }),
            );
          }}
        >
          View all
        </Button>
      </div>

      <div className="relative">
        {/* Scrollable row */}
        <div
          ref={scrollRef}
          className="flex gap-3 overflow-x-hidden"
          onScroll={handleScroll}
        >
          {connectedTypes.map((type) => (
            <IntegrationCompactCard key={type} integrationType={type} />
          ))}
        </div>

        {/* Right fade + chevron */}
        {canScrollRight && (
          <>
            <div className="absolute right-0 top-0 bottom-0 w-16 bg-gradient-to-l from-white via-white/80 to-transparent pointer-events-none" />
            <div className="absolute right-0 top-0 bottom-0 flex items-center">
              <Button
                icon
                variant="secondary"
                size="sm"
                onClick={() => scroll("right")}
                aria-label="Show more integrations"
              >
                <ChevronRight size={14} />
              </Button>
            </div>
          </>
        )}

        {/* Left fade + chevron */}
        {canScrollLeft && (
          <>
            <div className="absolute left-0 top-0 bottom-0 w-16 bg-gradient-to-r from-white via-white/80 to-transparent pointer-events-none" />
            <div className="absolute left-0 top-0 bottom-0 flex items-center">
              <Button
                icon
                variant="secondary"
                size="sm"
                onClick={() => scroll("left")}
                aria-label="Show previous integrations"
              >
                <ChevronLeft size={14} />
              </Button>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default EnabledIntegrationsRow;
